import { createFileRoute } from "@tanstack/react-router";
import { useState, useCallback, useMemo } from "react";
import { dealsList as dealsListData } from "../../../../../config";
import { fetcher } from "@/lib/utils";
import { directToBEFetcher } from "@/lib/utils";
import { generateSPVReceiptForDeal } from "@/lib/receipts/generate";
import type { DealData } from "@/lib/receipts/types";
import { toast } from "sonner";
import ButtonComponent from "@/components/ButtonComponent";

type DealStatus = "pending" | "running" | "success" | "skipped" | "error";

interface DealResult {
  dealId: string;
  status: DealStatus;
  message?: string;
}

async function processDeal(
  dealId: string,
): Promise<{ success: boolean; skipped?: boolean; message: string }> {
  const dealRes = await fetcher(`/api/deals/${dealId}`);
  if (dealRes?.error) {
    return {
      success: false,
      message: dealRes?.message || dealRes?.error || "Failed to fetch deal",
    };
  }

  const dealData = dealRes as DealData;
  if (dealData.type !== "spv") {
    return { success: true, skipped: true, message: "Not an SPV deal" };
  }
  if (!dealData.entity_id || !dealData.fm_identity_id) {
    return { success: true, skipped: true, message: "Missing entity or fund manager identity" };
  }

  const { file: receiptFile, receiptData } = await generateSPVReceiptForDeal(dealId, dealData);

  const formData = new FormData();
  formData.append("file", receiptFile);
  formData.append("type", "spv-agreement");

  const uploaded = await directToBEFetcher<{ file_id?: string }>(
    `/deals/${dealId}/files`,
    "POST",
    formData,
  );
  const uploadedFileId = uploaded?.file_id;
  if (!uploadedFileId) {
    return { success: false, message: "Upload succeeded but no file_id returned" };
  }

  const createRes = await fetcher("/api/legal-docs", "POST", {
    deal_id: dealId,
    status: "draft",
    file_id: uploadedFileId,
    type: "spv-agreement",
    metadata: receiptData as unknown as Record<string, unknown>,
  });
  if (createRes?.error) {
    return {
      success: false,
      message: createRes?.message || createRes?.error || "Failed to create legal document",
    };
  }

  return { success: true, message: "Generated and uploaded" };
}

function BatchGenerateLegalDocsContent() {
  const dealIds = useMemo(() => dealsListData.map((deal: any) => deal.id), []);

  const [results, setResults] = useState<DealResult[]>([]);
  const [running, setRunning] = useState(false);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);

  const runBatch = useCallback(async () => {
    if (running) return;
    setRunning(true);

    setResults(dealIds.map((id: string) => ({ dealId: id, status: "pending" as DealStatus })));

    for (let i = 0; i < dealIds.length; i++) {
      const dealId = dealIds[i];
      setCurrentIndex(i);
      setResults((prev) =>
        prev.map((r) => (r.dealId === dealId ? { ...r, status: "running" as DealStatus } : r)),
      );

      try {
        const outcome = await processDeal(dealId);
        setResults((prev) =>
          prev.map((r) =>
            r.dealId === dealId
              ? {
                  ...r,
                  status: outcome.skipped ? "skipped" : outcome.success ? "success" : "error",
                  message: outcome.message,
                }
              : r,
          ),
        );
        if (!outcome.success && !outcome.skipped) {
          toast.error(`Deal ${dealId}: ${outcome.message}`);
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err ?? "Unknown error");
        setResults((prev) =>
          prev.map((r) => (r.dealId === dealId ? { ...r, status: "error", message } : r)),
        );
        toast.error(`Deal ${dealId}: ${message}`);
      }
    }

    setCurrentIndex(null);
    setRunning(false);
    toast.success("Batch run finished. Check results below.");
  }, [running, dealIds]);

  const successCount = results.filter((r) => r.status === "success").length;
  const skippedCount = results.filter((r) => r.status === "skipped").length;
  const errorCount = results.filter((r) => r.status === "error").length;

  return (
    <div className="container max-w-4xl py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Batch Generate Legal Documents</h1>
        <p className="text-muted-foreground mt-1">
          Generates and uploads SPV agreement legal documents for all deal IDs ({dealIds.length}{" "}
          deals).
        </p>
      </div>

      <div className="flex items-center gap-4">
        <ButtonComponent variant="primary" onClick={runBatch} disabled={running}>
          {running
            ? `Processing ${(currentIndex ?? 0) + 1} / ${dealIds.length}...`
            : "Run for all deals"}
        </ButtonComponent>
        {!running && results.length > 0 && (
          <span className="text-sm text-muted-foreground">
            Last run: {successCount} success, {skippedCount} skipped, {errorCount} errors
          </span>
        )}
      </div>

      {results.length > 0 && (
        <div className="rounded-md border overflow-hidden">
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="text-left p-3 font-medium">#</th>
                  <th className="text-left p-3 font-medium">Deal ID</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-left p-3 font-medium">Message</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, idx) => (
                  <tr key={r.dealId} className="border-t">
                    <td className="p-3 text-muted-foreground">{idx + 1}</td>
                    <td className="p-3 font-mono text-xs">{r.dealId}</td>
                    <td className="p-3">
                      <span
                        className={
                          r.status === "success"
                            ? "text-green-600"
                            : r.status === "skipped"
                              ? "text-amber-600"
                              : r.status === "error"
                                ? "text-red-600"
                                : r.status === "running"
                                  ? "text-blue-600"
                                  : "text-muted-foreground"
                        }
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="p-3 text-muted-foreground">{r.message ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function RouteComponent() {
  return <BatchGenerateLegalDocsContent />;
}

export const Route = createFileRoute("/_authenticated/scripts/generate-legal-docs/")({
  component: RouteComponent,
  staticData: { page: "operations" },
});
